import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import Carousel from '@nerdwallet/react-carousel';
import FolderTabContainer from '../folder-tab-container/folder-tab-container';
import styles from './articles.module.scss';
import { CenteredContainer } from '@nerdwallet/currency';
import SegmentImpression from '~/app/components/segment-impression';
import { HOMEPAGE_SEGMENT_INSTRUMENTATION } from '~/app/lib/analytics/analytics-constants';
import { NewsArticlesQuery } from '~/generated-graphql/generated-types';
import ArticleCard from './article-card';
import CarouselCard from './carousel-card';
import Arrow from './arrow';
import BREAKING_NEWS_DATA from './breaking-news-data';

const carouselSettings = {
  arrows: true,
  slidesToShow: 3,
  variableWidth: true,
  dots: true,
  suppressHydrationWarning: true,
};

type ArticlesItems = NonNullable<NewsArticlesQuery['articles']>['items'];

const renderCarouselCards = (
  articles?: ArticlesItems,
  alternativeVersion?: boolean,
) => {
  if (!articles) return null;

  if (BREAKING_NEWS_DATA.useManualData) {
    return BREAKING_NEWS_DATA.articles.map((article, index) => (
      <CarouselCard
        index={index}
        key={article.id}
        id={article.id.toString()}
        title={article.title}
        authorImageUrl={article.authorImageUrl}
        authorLink={article.authorLink}
        author={article.author}
        articleLink={article.articleLink}
        date={article.displayDate ?? article.date}
        readTime={article.readTime}
        alternativeVersion={alternativeVersion}
      />
    ));
  }

  return articles.map((article, index) => (
    <CarouselCard
      index={index}
      key={article.id}
      id={article.id}
      title={article.title ?? ''}
      authorImageUrl={article.authors?.[0].avatarUrls?.[0].url}
      authorLink={article.authors?.[0].link}
      author={article.authors?.[0].name ?? ''}
      articleLink={article.link}
      date={article.displayDate ?? article.date ?? ''}
      readTime={article?.timeToRead?.minutes ?? 0}
      alternativeVersion={alternativeVersion}
    />
  ));
};

interface Props {
  articlesData?: NewsArticlesQuery | undefined;
  alternativeVersion?: boolean;
}

const Articles = ({
  articlesData,
  alternativeVersion = false,
}: Props): JSX.Element | null => {
  const desktopRef = useRef<HTMLDivElement>(null);

  // a11y: set the aria-label for carousel navigation buttons and inert attribute for hidden slides
  const injectAriaStyles = useCallback(() => {
    const rootRef = desktopRef.current;
    if (!rootRef) return;

    rootRef
      .querySelector('.slick-prev')
      ?.setAttribute('aria-label', 'Previous slide');
    rootRef
      .querySelector('.slick-next')
      ?.setAttribute('aria-label', 'Next slide');
    // a11y: fix for ARIA hidden element must not be focusable or contain focusable elements.
    rootRef
      .querySelectorAll<HTMLDivElement>('.slick-slide')
      .forEach((slide) => {
        const hidden = slide.getAttribute('aria-hidden') === 'true';
        slide.toggleAttribute('inert', hidden);
      });
  }, []);

  useEffect(() => {
    injectAriaStyles();
  }, [injectAriaStyles]);

  const carouselDesktopSettings = useMemo(
    () => ({
      ...carouselSettings,
      afterChange: injectAriaStyles, // fires after every slide change
    }),
    [injectAriaStyles],
  );

  if (!articlesData) return null;

  const featuredArticle = articlesData?.article;

  if (!featuredArticle) return null;

  return (
    <SegmentImpression
      eventName="Element Impression"
      eventProps={{
        entity_name: HOMEPAGE_SEGMENT_INSTRUMENTATION.breaking_news.entity_name,
        location: HOMEPAGE_SEGMENT_INSTRUMENTATION.breaking_news.location,
      }}
    >
      <div className={styles.desktopContainer} id="desktop-article">
        <FolderTabContainer
          tabBackgroundColor={alternativeVersion ? '#81D6B8' : '#40AD87'}
          negativeTopMarginValue={30}
        >
          <div
            className={
              alternativeVersion
                ? styles.alternativeBackgroundContainer
                : styles.backgroundContainer
            }
          >
            {/* Desktop Content */}
            <CenteredContainer>
              <FolderTabContainer
                tabBackgroundColor={alternativeVersion ? '#E3FAF2' : '#B7ECDA'}
                contentBackgroundColor={
                  alternativeVersion ? '#E3FAF2' : '#B7ECDA'
                }
                contentClassName={styles.desktopArticleContainer}
                roundedCorners
              >
                <div className={styles.articleHeader}>
                  <h3 className={styles.articleHeaderTitle}>
                    Stay up to date with the latest financial news from the
                    Nerds
                  </h3>
                  <a
                    className={styles.articleHeaderLink}
                    href="https://www.nerdwallet.com/h/news/financial-news"
                  >
                    <div className={styles.viewAllNewsLink}>
                      <span>View all news</span>
                      <Arrow />
                    </div>
                  </a>
                </div>
                <ArticleCard
                  id={featuredArticle.id}
                  title={featuredArticle.title ?? ''}
                  imageUrl={featuredArticle.featuredMedia?.sourceUrl ?? ''}
                  authorImageUrl={
                    featuredArticle.authors?.[0].avatarUrls?.[0].url
                  }
                  authorLink={featuredArticle.authors?.[0].link}
                  author={featuredArticle.authors?.[0].name ?? ''}
                  articleLink={featuredArticle.link}
                  date={
                    featuredArticle?.displayDate ?? featuredArticle.date ?? ''
                  }
                  readTime={featuredArticle?.timeToRead?.minutes ?? 0}
                  alternativeVersion={alternativeVersion}
                />
                <div ref={desktopRef} className={styles.carouselContainer}>
                  <Carousel
                    className="articles-carousel"
                    {...carouselDesktopSettings}
                  >
                    {renderCarouselCards(
                      articlesData?.articles?.items,
                      alternativeVersion,
                    )}
                  </Carousel>
                </div>
              </FolderTabContainer>
            </CenteredContainer>
          </div>
        </FolderTabContainer>
      </div>

      {/* Mobile Content */}
      <div className={styles.mobileContainer}>
        <FolderTabContainer
          tabBackgroundColor={alternativeVersion ? '#81D6B8' : '#40AD87'}
          negativeTopMarginValue={alternativeVersion ? 63 : 30}
        >
          <div
            className={
              alternativeVersion
                ? styles.alternativeBackgroundContainer
                : styles.backgroundContainer
            }
          >
            <CenteredContainer>
              <div className={styles.articleHeader}>
                <h3 className={styles.articleHeaderTitle}>
                  Stay up to date with the latest financial news from the Nerds
                </h3>
              </div>
              <FolderTabContainer
                tabBackgroundColor="#E3FAF2"
                contentBackgroundColor="#E3FAF2"
                roundedCorners
                folderTabWidthPercentage={50}
              >
                <ArticleCard
                  id={featuredArticle.id}
                  title={featuredArticle.title ?? ''}
                  imageUrl={featuredArticle.featuredMedia?.sourceUrl ?? ''}
                  authorImageUrl={
                    featuredArticle.authors?.[0].avatarUrls?.[0].url
                  }
                  authorLink={featuredArticle.authors?.[0].link}
                  author={featuredArticle.authors?.[0].name ?? ''}
                  articleLink={featuredArticle.link}
                  date={
                    featuredArticle?.displayDate ?? featuredArticle?.date ?? ''
                  }
                  readTime={featuredArticle?.timeToRead?.minutes ?? 0}
                  alternativeVersion={alternativeVersion}
                />
              </FolderTabContainer>
            </CenteredContainer>
            <div className={styles.carouselContainer}>
              <Carousel
                className="articles-carousel"
                {...{ ...carouselSettings, slidesToShow: 1.1, arrows: false }}
              >
                {renderCarouselCards(
                  articlesData?.articles?.items,
                  alternativeVersion,
                )}
              </Carousel>
            </div>
          </div>
        </FolderTabContainer>
      </div>
    </SegmentImpression>
  );
};

export default Articles;
